<template>
  <div
    ref="containerEl"
    class="run-track relative flex flex-col items-center"
    :class="classes"
    :data-branch-terminates="nodesTerminates || undefined"
  >
    <template
      v-for="(node, index) in nodes"
      :key="node._uuid"
    >
      <div
        v-if="canRenderNode(node)"
        class="run-track-item flex flex-col items-center"
        :class="{ 'opacity-50': isUnreachable(index) }"
      >
        <!-- Dropzone before each node for spacing -->
        <!-- Always show dropzone for proper spacing, but disable interaction if unreachable -->
        <Dropzone
          :disabled="isUnreachable(index)"
          :parent-uuid="parentUuid"
          :index="index"
          @dropped-node="handleDroppedNode(index, $event)"
        />

        <component
          :is="nodeComponent(node.type)"
          class="flowbuilder-node-component"
          :class="{ 'flowbuilder-unreachable': isUnreachable(index) }"
          :node="node"
          v-bind="getNodeProps(node)"
          @node-action="handleNodeAction(index, $event)"
          @delete-node="handleNodeAction(index, 'delete')"
          @click="handleNodeAction(index, 'click')"
        />
      </div>
    </template>

    <!-- Final dropzone after all nodes for spacing -->
    <Dropzone
      v-if="!nodesTerminates"
      :parent-uuid="parentUuid"
      :index="nodes.length"
      @dropped-node="handleDroppedNode(nodes.length, $event)"
    />
  </div>
</template>

<script setup lang="ts">
/**
 * NodeRun Component
 * 
 * Renders a vertical sequence of flow nodes with proper spacing and connections.
 * This is the workhorse component that handles:
 * - Node rendering based on type
 * - Unreachable node detection (greyed out)
 * - Dropzone slots for adding new nodes
 * - Node action event propagation
 * 
 * Used by RootNode and split nodes to render their children
 */
import { computed, ref, inject, provide } from 'vue'
import type { FunnelDefinitionNode, NodeOffer } from '../../types'
import { doesNodeTerminate, uuid, funnelDefinitionEach } from '../../services'

// Import all node components
// Each node type has its own component for specialized rendering
import Dropzone from './Dropzone.vue'
import SplitNode from './SplitNode.vue'
import YesNoNode from './YesNoNode.vue'
import OfferNode from './OfferNode.vue'
import ExitNode from './ExitNode.vue'
import RedirectNode from './RedirectNode.vue'
import CodeNode from './CodeNode.vue'
import MessageNode from './MessageNode.vue'
import DataNode from './DataNode.vue'
import WebhookNode from './WebhookNode.vue'
import HighScoreNode from './HighScoreNode.vue'

const props = defineProps<{
  nodes: FunnelDefinitionNode[]
  classes?: string[]
  showFinalDropzone?: boolean
  inRedirectFunnel?: boolean
  parentNode?: any // Parent node for connection tracking
}>()

const emit = defineEmits<{
  'node-action': [index: number, action: string, data?: any]
  'nodes-updated': [nodes: FunnelDefinitionNode[]]
}>()

const containerEl = ref<HTMLElement | null>(null)

provide('inRedirectFunnel', props.inRedirectFunnel)

// Inject dependencies at setup time
const adapter = inject('adapter') as any

// Inject the consolidated nodeProps object during setup
const nodePropsData = inject('nodeProps', ref({
  icons: {},
  offers: {},
  getExternalUrl: (url: string) => url,
  customFieldLabel: async (id: string) => id,
  tagLabel: async (id: string) => id,
  describeLogic: async () => 'Complex condition',
}))

// Also inject questions and segmentGroups separately as they're used directly
const flow = inject('flow')
const questions = inject('questions', ref([]))
const segmentGroups = inject('segmentGroups', ref([]))

/**
 * Parent UUID for dropzone identification
 * Used by the Vimium-style hint system to identify dropzone positions
 */
const parentUuid = computed(() => {
  // Handle different parent structures - could be node._uuid or path-level identifier
  if (props.parentNode?._uuid) return props.parentNode._uuid
  if (props.parentNode?.segmentId) return props.parentNode.segmentId
  return 'root'
})

/**
 * Terminal Node Detection
 *
 * Determines if the last node in this run terminates the flow.
 * This affects whether we show a dropzone at the end for adding more nodes.
 */
const nodesTerminates = computed(() => {
  const lastNode = props.nodes[props.nodes.length - 1]
  if (!lastNode) return false

  // Force reactivity on code node's code property
  if (lastNode.type === 'code') {
    // Access the code property to ensure Vue tracks it
    const _ = lastNode.code
  }

  return doesNodeTerminate(lastNode)
})

/**
 * Computed array tracking which nodes terminate
 * This ensures reactivity when code node content changes
 */
const nodeTerminationStatus = computed(() => {
  return props.nodes.map((node) => {
    // Force reactivity on code node's code property
    if (node.type === 'code') {
      const _ = node.code
    }
    return doesNodeTerminate(node)
  })
})

/**
 * Unreachable Node Detection
 *
 * A node is unreachable if any node before it terminates the flow.
 * Unreachable nodes are shown with reduced opacity to indicate they won't execute.
 * This helps users understand flow logic at a glance.
 */
const isUnreachable = (index: number): boolean => {
  // Access the computed to ensure reactivity
  const _ = nodeTerminationStatus.value

  const nodesPriorToIndex = props.nodes.slice(0, index)
  return nodesPriorToIndex.some((node) => {
    return doesNodeTerminate(node) && canRenderNode(node)
  })
}

/**
 * Node Type to Component Mapping
 * 
 * Maps node type strings to their corresponding Vue components.
 * This allows dynamic component rendering based on node data.
 */
const nodeComponent = (nodeType: string) => {
  switch (nodeType) {
    case 'sg':
    case 'random':
      return SplitNode
    case 'yesno':
      return YesNoNode
    case 'offer':
      return OfferNode
    case 'exit':
      return ExitNode
    case 'redirect':
      return RedirectNode
    case 'code':
      return CodeNode
    case 'message':
      return MessageNode
    case 'data':
      return DataNode
    case 'webhook':
      return WebhookNode
    case 'highscore':
      return HighScoreNode
    case 'aigenerate':
      // For now, return a placeholder
      return 'div'
    default:
      return 'div'
  }
}

// Check if we can render this node type
const canRenderNode = (node: FunnelDefinitionNode): boolean => {
  if (props.inRedirectFunnel) {
    return node.type !== 'offer'
  }
  return true
}

// Handle actions from child nodes
const handleNodeAction = (index: number, action: string, data?: any) => {
  const targetNode = props.nodes[index]
  if (!targetNode) return

  if (action === 'click' && adapter?.onNodeClick) {
    // console.log('click', targetNode)
    //adapter.onNodeClick(targetNode)
  } else if (action === 'delete' && adapter?.onNodeDelete) {
    // Notify about connection change before removing
    if (adapter?.onConnectedNodesChange && props.parentNode) {
      adapter.onConnectedNodesChange({
        action: 'remove',
        parentNode: props.parentNode,
        childNode: targetNode,
        index: index
      })
    }
    props.nodes.splice(index, 1)
  }
  //emit('node-action', index, action, data)
}

/**
 * Handle dropped node from dropzone
 * Adds new node or moves existing node to the specified index
 */
const handleDroppedNode = (index: number, droppedData: any) => {
  let subtractFromIndex = false

  // Handle new dropzone data structure { node, isMove, isClone }
  const node = droppedData.node || droppedData
  const isMove = droppedData.isMove || false
  const isClone = droppedData.isClone || false

  // Check if this is an existing node being moved
  if (isMove && node._uuid) {
    const funnel = flow.value.funnelDefinition

    if (funnel) {
      // Find and remove the existing node from its current location
      funnelDefinitionEach(funnel, (n, nuke) => {
        if (n._uuid === node._uuid) {
          nuke()
        }
      })
    }

    // Check if the original node was before our target index
    const idx = props.nodes.findIndex((n) => n._uuid === node._uuid)
    if (idx > -1 && idx < index) {
      subtractFromIndex = true
    }
  } else if (!node._uuid) {
    // New node, ensure it has a UUID
    node._uuid = uuid()
  }


  // Insert the node at the target location
  const newNodes = [...props.nodes]
  const insertIndex = subtractFromIndex ? index - 1 : index
  newNodes.splice(insertIndex, 0, node)
  emit('nodes-updated', newNodes)

  // Notify about connection change
  if (adapter?.onConnectedNodesChange && props.parentNode) {
    adapter.onConnectedNodesChange({
      action: isMove ? 'move' : 'add',
      parentNode: props.parentNode,
      childNode: node,
      index: insertIndex,
      isClone
    })
  }

  // Reset drag state
  if (adapter?.setDragging) {
    adapter.setDragging(null)
  }
}

/**
 * Get component-specific props based on node type
 * This allows each node type to receive only the props it needs
 */
const getNodeProps = (node: FunnelDefinitionNode) => {
  const baseProps = {}

  switch (node.type) {
    case 'sg':
    case 'random':
      return {
        ...baseProps,
        segmentGroups: segmentGroups.value || [],
        questions: questions.value || [],
        icon: nodePropsData.value.icons?.QuestionIcon,
        LoopIcon: nodePropsData.value.icons?.LoopIcon,
        OpenEndedIcon: nodePropsData.value.icons?.OpenEndedIcon,
      }
    case 'offer':
      return {
        ...baseProps,
        CtaIcon: nodePropsData.value.icons?.GiftIcon,
        FormIcon: nodePropsData.value.icons?.InboxIcon,
      }
    case 'data':
      return {
        ...baseProps,
        CodeIcon: nodePropsData.value.icons?.CodeBracketIcon,
        customFieldLabel: nodePropsData.value.customFieldLabel,
        tagLabel: nodePropsData.value.tagLabel,
      }
    case 'yesno':
      return {
        ...baseProps,
        FunnelIcon: nodePropsData.value.icons?.FunnelIcon,
      }
    case 'webhook':
      return baseProps
    case 'code':
      return {
        ...baseProps,
        CodeIcon: nodePropsData.value.icons?.CodeBracketIcon,
      }
    case 'highscore':
      return {
        ...baseProps,
        CodeIcon: nodePropsData.value.icons?.CodeBracketIcon,
      }
    default:
      return baseProps
  }
}
</script>
